/**
 * Space scenes: a ray-traced black hole, a total solar eclipse, Saturn, a planetarium and a nebula
 * fly-through. All procedural (no image textures).
 */
import type { VisualizationDef } from '../../../shared/viz';

/** Helpers shared by the space scenes. Unused functions are dropped by the GLSL compiler. */
const SPACE = /* glsl */ `
mat2 rot2(float a) { float c = cos(a), s = sin(a); return mat2(c, -s, s, c); }
float hash31(vec3 p) {
  p = fract(p * 0.1031);
  p += dot(p, p.zyx + 31.32);
  return fract((p.x + p.y) * p.z);
}
float noise3(vec3 p) {
  vec3 i = floor(p), f = fract(p);
  vec3 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(mix(hash31(i), hash31(i + vec3(1, 0, 0)), u.x), mix(hash31(i + vec3(0, 1, 0)), hash31(i + vec3(1, 1, 0)), u.x), u.y),
    mix(mix(hash31(i + vec3(0, 0, 1)), hash31(i + vec3(1, 0, 1)), u.x), mix(hash31(i + vec3(0, 1, 1)), hash31(i + vec3(1, 1, 1)), u.x), u.y),
    u.z);
}
float fbm3(vec3 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 5; i++) { v += a * noise3(p); p = p * 2.02 + vec3(11.7, 3.1, 7.9); a *= 0.5; }
  return v;
}
// Stars in a direction: sparse points, a few brighter and tinted, twinkling with the treble.
vec3 starfield(vec3 d) {
  vec3 col = vec3(0.0);
  for (int k = 0; k < 2; k++) {
    float scale = k == 0 ? 160.0 : 60.0;
    vec3 p = d * scale;
    vec3 id = floor(p);
    float h = hash31(id + float(k) * 17.0);
    if (h > (k == 0 ? 0.92 : 0.985)) {
      vec3 c = id + 0.5 + (vec3(hash31(id + 1.3), hash31(id + 2.7), hash31(id + 5.1)) - 0.5) * 0.6;
      float dist = length(p - c);
      float tw = 0.75 + 0.25 * sin(uTime * (2.0 + h * 9.0) + h * 40.0) * (0.4 + uTreble);
      vec3 tint = mix(vec3(0.7, 0.8, 1.0), vec3(1.0, 0.85, 0.7), hash31(id + 9.9));
      col += tint * smoothstep(0.18, 0.0, dist) * tw * (k == 0 ? 0.7 : 1.6);
    }
  }
  return col;
}
// Ray/sphere: distance to the first hit, or -1.
float sphereHit(vec3 ro, vec3 rd, vec3 c, float r) {
  vec3 oc = ro - c;
  float b = dot(oc, rd);
  float h = b * b - dot(oc, oc) + r * r;
  if (h < 0.0) return -1.0;
  float t = -b - sqrt(h);
  return t > 0.0 ? t : -1.0;
}
// Camera basis looking from ro at the target.
mat3 lookAt(vec3 ro, vec3 target) {
  vec3 f = normalize(target - ro);
  vec3 r = normalize(cross(vec3(0.0, 1.0, 0.0), f));
  return mat3(r, cross(f, r), f);
}
`;

export const blackHole: VisualizationDef = {
  id: 'black-hole',
  name: 'Black Hole',
  description:
    'A black hole traced with the exact Schwarzschild light-bending equation: the far side of the disk arcs over the top and under the bottom, the shadow has its true size, and the disk shows Doppler beaming and gravitational redshift. The bass heats the disk.',
  tags: ['energy:mid', 'style:space'],
  role: 'background',
  params: [
    { id: 'distance', label: 'Distance', type: 'float', min: 10, max: 40, default: 20 },
    { id: 'tilt', label: 'Tilt', type: 'float', min: 0.02, max: 0.8, default: 0.12 },
    {
      id: 'detail',
      label: 'Detail',
      type: 'float',
      min: 80,
      max: 220,
      default: 170,
      step: 1,
      quality: true,
    },
    { id: 'spin', label: 'Disk spin', type: 'float', min: 0, max: 3, default: 1 },
    { id: 'doppler', label: 'Doppler beaming', type: 'float', min: 0, max: 1, default: 0.8 },
    { id: 'colour', label: 'Palette colour', type: 'float', min: 0, max: 1, default: 0.25 },
    { id: 'orbit', label: 'Camera orbit', type: 'float', min: -1, max: 1, default: 0.15 },
  ],
  fragment: /* glsl */ `
${SPACE}
// Units: Schwarzschild radius = 1 (so M = 0.5). Photon sphere r = 1.5, shadow radius b = 3√3/2.
const float RIN = 3.0;   // innermost stable circular orbit
const float ROUT = 10.0;
const float DPHI = 0.06; // orbit-angle step (RK4): shadow radius within ~0.3% of exact

vec3 blackbody(float t) {
  return mix(vec3(1.0, 0.28, 0.05), vec3(1.0, 0.75, 0.45), smoothstep(0.2, 0.7, t)) + vec3(0.3, 0.45, 0.9) * smoothstep(0.75, 1.1, t);
}

// Emission and opacity where the light path crosses the disk plane. dir: direction along the
// traced path (camera → disk), so the photon travels along -dir.
vec4 disk(vec3 p, vec3 dir) {
  float r = length(p.xz);
  float a = atan(p.z, p.x) - uTime * 0.6 * uP[3] * inversesqrt(r * r * r) * 8.0;
  float n = fbm(vec2(r * 3.2, 0.0) + vec2(cos(a), sin(a)) * 2.2);
  n = 0.35 + 1.3 * n * n;
  float temp = pow(RIN / r, 0.75);
  float edge = smoothstep(RIN, RIN * 1.12, r) * smoothstep(ROUT, ROUT * 0.65, r);
  // Circular-orbit speed seen by a static observer, Doppler factor, and the gravitational
  // redshift of climbing out of the well: together g = sqrt(1 - 1.5/r) / (γ-free) (1 - β cosθ).
  vec3 vdir = normalize(vec3(-p.z, 0.0, p.x));
  float beta = sqrt(0.5 / (r - 1.0));
  float cosT = dot(vdir, -normalize(dir));
  float g = sqrt(1.0 - 1.5 / r) / (1.0 - beta * cosT);
  float boost = mix(1.0, g * g * g, uP[4]);
  vec3 c = mix(blackbody(temp * mix(1.0, g, uP[4]) * 0.9), palette(temp * 0.9 + 0.05), uP[5]);
  float I = n * temp * edge * boost * (0.8 + 0.9 * uBass + 0.35 * pulse());
  return vec4(c * I * 1.6, clamp(I * 0.9, 0.0, 0.95) * edge);
}

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float camDist = uP[0];
  float incl = uP[1];
  vec3 ro = vec3(0.0, camDist * sin(incl), -camDist * cos(incl));
  ro.xz = rot2(uTime * 0.03 * uP[6]) * ro.xz;
  mat3 cam = lookAt(ro, vec3(0.0));
  vec3 rd = cam * normalize(vec3(uv, 1.7));

  // Light around a non-rotating black hole stays in one plane: trace u = 1/r against the orbit
  // angle with the exact equation u'' + u = 1.5 u², then map back to 3D.
  float r0 = length(ro);
  vec3 e1 = ro / r0;
  vec3 nrm = cross(e1, rd);
  float nl = length(nrm);
  if (nl < 1e-5) { fragColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
  vec3 e2 = (rd - dot(rd, e1) * e1) / nl;
  float u = 1.0 / r0;
  float du = -u * dot(rd, e1) / nl;
  float phi = 0.0;
  vec3 prevP = ro;
  vec3 dir = rd;
  vec3 col = vec3(0.0);
  float trans = 1.0;
  float umax = u;
  bool captured = false;
  float steps = uP[2];
  for (int i = 0; i < 220; i++) {
    if (float(i) >= steps) { captured = true; break; } // still circling the photon sphere
    vec2 k1 = vec2(du, 1.5 * u * u - u);
    vec2 s2 = vec2(u, du) + 0.5 * DPHI * k1; vec2 k2 = vec2(s2.y, 1.5 * s2.x * s2.x - s2.x);
    vec2 s3 = vec2(u, du) + 0.5 * DPHI * k2; vec2 k3 = vec2(s3.y, 1.5 * s3.x * s3.x - s3.x);
    vec2 s4 = vec2(u, du) + DPHI * k3;       vec2 k4 = vec2(s4.y, 1.5 * s4.x * s4.x - s4.x);
    vec2 st = vec2(u, du) + DPHI / 6.0 * (k1 + 2.0 * k2 + 2.0 * k3 + k4);
    u = st.x; du = st.y;
    phi += DPHI;
    if (u >= 1.0) { captured = true; break; }       // crossed the event horizon
    if (u <= 0.0) break;                             // escaped to infinity
    umax = max(umax, u);
    vec3 P = (cos(phi) * e1 + sin(phi) * e2) / u;
    dir = P - prevP;
    if (prevP.y * P.y < 0.0) {
      vec3 hit = mix(prevP, P, prevP.y / (prevP.y - P.y));
      float rh = length(hit.xz);
      if (rh > RIN && rh < ROUT) {
        vec4 d = disk(hit, dir);
        col += trans * d.rgb;
        trans *= 1.0 - d.a;
        if (trans < 0.02) break;
      }
    }
    prevP = P;
  }
  if (!captured) {
    col += trans * starfield(normalize(dir)) * 0.8;
    // Photon ring: light that skimmed the photon sphere (r = 1.5).
    col += trans * palette(0.9) * exp(-(1.0 / umax - 1.5) * 9.0) * (0.25 + 0.9 * pulse());
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const solarEclipse: VisualizationDef = {
  id: 'solar-eclipse',
  name: 'Solar Eclipse',
  description:
    'Totality: the corona streams out around the moon, each angle following a frequency band. Beads of sunlight and the diamond ring flash on the big hits.',
  tags: ['energy:low', 'style:space'],
  role: 'background',
  params: [
    { id: 'size', label: 'Moon size', type: 'float', min: 0.5, max: 1.6, default: 1 },
    { id: 'reach', label: 'Corona reach', type: 'float', min: 0, max: 2, default: 1 },
    { id: 'colour', label: 'Palette colour', type: 'float', min: 0, max: 1, default: 0.3 },
    { id: 'ring', label: 'Diamond ring', type: 'float', min: 0, max: 2, default: 1 },
  ],
  fragment: /* glsl */ `
${SPACE}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float R = 0.2 * uP[0];
  float r = length(p);
  float a = atan(p.y, p.x);
  vec3 sky = mix(vec3(0.01, 0.015, 0.04), vec3(0.03, 0.04, 0.09), smoothstep(0.9, 0.0, r));
  vec3 col = sky + starfield(normalize(vec3(p, 1.2))) * 0.5;
  float x = max(r / R - 1.0, 0.0);
  // The spectrum wraps around the rim, mirrored so it is symmetric.
  float band = spectrum(abs(a) / PI * 0.9);
  float s = fbm(vec2(a * 3.0, x * 1.2 - uTime * 0.04)) ;
  float streak = fbm(vec2(a * 14.0, x * 0.3 - uTime * 0.02));
  float L = (0.18 + band * 1.2 * uP[1] + 0.35 * s) * (0.6 + 0.4 * streak);
  float corona = exp(-x / max(L, 0.02)) * (0.35 + s) + exp(-x * 10.0) * 1.2;
  vec3 tint = mix(vec3(1.0, 0.96, 0.9), palette(0.55 + x * 0.3), uP[2]);
  col += tint * corona * (0.7 + 0.25 * uLevel) * step(R, r);
  // Prominences: small pink loops at the limb.
  float prom = smoothstep(0.72, 0.86, fbm(vec2(a * 10.0, uTime * 0.08))) * exp(-x * 28.0);
  col += vec3(1.0, 0.25, 0.45) * prom * 1.4 * step(R, r);
  // Baily's beads and the diamond ring.
  float flash = pulse() * pulse() * uP[3];
  vec2 rim = R * vec2(cos(0.75), sin(0.75));
  vec2 dp = p - rim;
  float ring = exp(-length(dp) * 70.0) * 3.0 + exp(-abs(dp.y) * 420.0) * exp(-abs(dp.x) * 5.0) + exp(-abs(dp.x) * 420.0) * exp(-abs(dp.y) * 5.0);
  float beads = 0.0;
  for (int i = 0; i < 5; i++) {
    float ba = 0.45 + float(i) * 0.12;
    beads += exp(-length(p - R * vec2(cos(ba), sin(ba))) * 220.0) * hash21(vec2(float(i), floor(uTime * 8.0)));
  }
  col += vec3(1.0, 0.97, 0.9) * (ring + beads * 2.0) * flash;
  // The moon itself, with faint earthshine.
  col = mix(col, vec3(0.012, 0.012, 0.016) + palette(0.05) * 0.02, smoothstep(R + 0.002, R - 0.002, r));
  fragColor = vec4(col, 1.0);
}`,
};

export const saturn: VisualizationDef = {
  id: 'saturn',
  name: 'Saturn',
  description:
    'Saturn and its rings, lit by the Sun, with real shadows. Each ringlet is a frequency band, so the rings work as a spectrum analyser; spokes flicker on the beat.',
  tags: ['energy:low', 'style:space'],
  role: 'background',
  params: [
    { id: 'tilt', label: 'Tilt', type: 'float', min: 0.05, max: 0.9, default: 0.3 },
    { id: 'zoom', label: 'Zoom', type: 'float', min: 0.6, max: 2, default: 1 },
    { id: 'react', label: 'Ring response', type: 'float', min: 0, max: 2, default: 1 },
    { id: 'colour', label: 'Palette colour', type: 'float', min: 0, max: 1, default: 0.35 },
    { id: 'orbit', label: 'Camera orbit', type: 'float', min: -1, max: 1, default: 0.2 },
  ],
  fragment: /* glsl */ `
${SPACE}
const float RIN = 1.24, ROUT = 2.28;
const vec3 SUN = vec3(-0.62, 0.32, -0.72);

// Ring opacity at radius r: ringlets, the Cassini division and the Encke gap.
float ringDensity(float r) {
  if (r < RIN || r > ROUT) return 0.0;
  float n = 0.55 + 0.3 * sin(r * 70.0) * sin(r * 23.0) + 0.25 * (noise2(vec2(r * 90.0, 0.5)) - 0.5);
  n *= smoothstep(0.015, 0.045, abs(r - 1.95));   // Cassini division
  n *= smoothstep(0.004, 0.01, abs(r - 2.21));    // Encke gap
  n *= mix(0.45, 1.0, smoothstep(RIN, 1.5, r));    // faint C ring inside
  return clamp(n, 0.0, 1.0);
}

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  vec3 L = normalize(SUN);
  float d = 6.0 / uP[1];
  vec3 ro = vec3(0.0, d * sin(uP[0]), -d * cos(uP[0]));
  ro.xz = rot2(uTime * 0.02 * uP[4]) * ro.xz;
  mat3 cam = lookAt(ro, vec3(0.0));
  vec3 rd = cam * normalize(vec3(uv, 1.8));
  vec3 col = starfield(rd) * 0.6;

  // Planet: an oblate sphere (scale y by 1.1 to make it round in a unit sphere).
  vec3 so = vec3(ro.x, ro.y * 1.1, ro.z), sd = vec3(rd.x, rd.y * 1.1, rd.z);
  float sl = length(sd);
  float tp = sphereHit(so, sd / sl, vec3(0.0), 1.0) / sl;
  float tr = abs(rd.y) > 1e-4 ? -ro.y / rd.y : -1.0;

  vec3 planet = vec3(0.0);
  if (tp > 0.0) {
    vec3 P = ro + rd * tp;
    vec3 N = normalize(vec3(P.x, P.y * 1.21, P.z));
    float lat = N.y;
    float bands = fbm(vec2(lat * 14.0, atan(N.z, N.x) * 0.4 + uTime * 0.01));
    vec3 base = mix(vec3(0.85, 0.74, 0.55), vec3(0.62, 0.5, 0.36), bands);
    base = mix(base, palette(0.3 + lat * 0.5 + bands * 0.2), uP[3]);
    float lit = max(dot(N, L), 0.0);
    // Shadow of the rings on the planet.
    float ts = -P.y / L.y;
    float shadow = 1.0;
    if (ts > 0.0) shadow = 1.0 - 0.85 * ringDensity(length((P + L * ts).xz));
    planet = base * (0.04 + lit * shadow) * (0.9 + 0.2 * uLevel);
    planet += vec3(0.6, 0.7, 1.0) * pow(1.0 - max(dot(N, -rd), 0.0), 3.0) * 0.15 * lit;
    col = planet;
  }
  if (tr > 0.0 && (tp < 0.0 || tr < tp)) {
    vec3 Q = ro + rd * tr;
    float r = length(Q.xz);
    float dens = ringDensity(r);
    if (dens > 0.0) {
      float x = (r - RIN) / (ROUT - RIN);
      float band = spectrum(x * 0.9);
      // Is this bit of ring in the planet's shadow?
      vec3 qo = vec3(Q.x, Q.y * 1.1, Q.z), ql = vec3(L.x, L.y * 1.1, L.z);
      float inShadow = sphereHit(qo, normalize(ql), vec3(0.0), 1.0) > 0.0 ? 0.08 : 1.0;
      float ang = atan(Q.z, Q.x);
      float spokes = smoothstep(0.6, 1.0, noise2(vec2(ang * 18.0, r * 3.0 + uTime * 0.2))) * pulse();
      vec3 rc = mix(vec3(0.92, 0.84, 0.7), palette(x), uP[3]);
      vec3 ringCol = rc * (0.35 + band * 1.3 * uP[2]) * (0.25 + 0.75 * abs(L.y) * 2.0) * inShadow * (1.0 - 0.4 * spokes);
      col = mix(col, ringCol, dens * 0.9);
    }
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const planetarium: VisualizationDef = {
  id: 'planetarium',
  name: 'Planetarium',
  description:
    'A little solar system: planets on Kepler orbits around a glowing star, each one swelling with its own frequency band. A gas giant has moons and bands.',
  tags: ['energy:mid', 'style:space'],
  role: 'background',
  params: [
    { id: 'speed', label: 'Orbit speed', type: 'float', min: 0, max: 3, default: 1 },
    { id: 'tilt', label: 'Tilt', type: 'float', min: 0.15, max: 1, default: 0.45 },
    { id: 'swell', label: 'Swell', type: 'float', min: 0, max: 2, default: 1 },
    { id: 'orbits', label: 'Orbit lines', type: 'float', min: 0, max: 1, default: 0.5 },
  ],
  fragment: /* glsl */ `
${SPACE}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float tilt = uP[1];
  vec3 col = starfield(normalize(vec3(p, 1.0))) * 0.5;
  // Star.
  float rs = length(p);
  col += palette(0.95) * (exp(-rs * 18.0) * 2.0 + 0.004 / (rs * rs + 0.004) * 0.25) * (0.8 + 0.5 * uBass);
  col += vec3(1.0, 0.95, 0.85) * smoothstep(0.045, 0.035, rs);
  for (int i = 0; i < 7; i++) {
    float fi = float(i);
    float a = 0.11 + fi * 0.065;              // orbit radius (screen units)
    float speed = uP[0] * 0.6 / pow(a / 0.11, 1.5); // Kepler's third law
    float th = hash21(vec2(fi, 3.0)) * TAU + uTime * speed;
    vec2 pos = vec2(cos(th) * a, sin(th) * a * tilt);
    // Orbit line.
    vec2 e = vec2(p.x, p.y / tilt);
    col += palette(fi / 7.0) * smoothstep(0.0025, 0.0, abs(length(e) - a)) * 0.15 * uP[3];
    float band = uBands[i];
    float size = (0.008 + 0.006 * hash21(vec2(fi, 7.0)) + (i == 4 ? 0.014 : 0.0)) * (1.0 + band * uP[2]);
    vec2 d = p - pos;
    float dist = length(d);
    // Planets in front of the star (lower half of the ellipse) cover it; behind, the star covers them.
    bool behind = sin(th) > 0.0 && length(pos) < 0.05;
    if (dist < size && !behind) {
      vec3 n = vec3(d / size, sqrt(max(0.0, 1.0 - dot(d, d) / (size * size))));
      vec3 toStar = normalize(vec3(-pos, 0.02));
      float lit = max(dot(n, toStar), 0.0);
      vec3 base = palette(fi / 7.0 + 0.1);
      if (i == 4) base *= 0.7 + 0.3 * sin(n.y * 25.0 + fbm(n.xy * 6.0) * 3.0); // gas giant bands
      col = base * (0.05 + 1.1 * lit) * (0.9 + 0.4 * band);
    }
    col += palette(fi / 7.0 + 0.1) * exp(-dist / size * 2.5) * 0.25 * band;
    if (i == 4) {
      for (int m = 0; m < 3; m++) {
        float fm = float(m);
        float mth = uTime * uP[0] * (2.5 - fm * 0.6) + fm * 2.0;
        vec2 mp = pos + vec2(cos(mth), sin(mth) * tilt) * size * (2.0 + fm * 0.8);
        col += vec3(0.85) * smoothstep(0.004, 0.002, length(p - mp));
      }
    }
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const nebula: VisualizationDef = {
  id: 'nebula',
  name: 'Nebula',
  description:
    'Flying through glowing interstellar gas lit by newborn stars; the gas thickens with the bass.',
  tags: ['energy:low', 'style:space'],
  role: 'background',
  params: [
    { id: 'speed', label: 'Speed', type: 'float', min: 0, max: 3, default: 1 },
    { id: 'density', label: 'Density', type: 'float', min: 0.2, max: 2, default: 1 },
    {
      id: 'detail',
      label: 'Detail',
      type: 'float',
      min: 12,
      max: 64,
      default: 28,
      step: 1,
      quality: true,
    },
    { id: 'glow', label: 'Glow', type: 'float', min: 0.2, max: 2, default: 1 },
  ],
  fragment: /* glsl */ `
${SPACE}
// Four octaves are plenty for soft gas (and this runs per step, per pixel).
float gas(vec3 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * noise3(p); p = p * 2.02 + vec3(11.7, 3.1, 7.9); a *= 0.5; }
  return v / 0.9375;
}
void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float z = uTime * 0.25 * uP[0];
  vec3 ro = vec3(0.3 * sin(z * 0.3), 0.2 * cos(z * 0.23), z);
  vec3 rd = normalize(vec3(uv, 1.3));
  rd.xy = rot2(sin(uTime * 0.03) * 0.3) * rd.xy;
  vec3 col = vec3(0.0);
  float trans = 1.0;
  float steps = uP[2];
  float dt = 6.0 / steps;
  for (int i = 0; i < 64; i++) {
    if (float(i) >= steps || trans < 0.03) break;
    float t = 0.2 + float(i) * dt;
    vec3 pos = ro + rd * t;
    float d = gas(pos * 0.5) * 0.97 - 0.52 + 0.06 * uBass;
    d = max(d, 0.0) * 5.0 * uP[1];
    if (d > 0.0) {
      float hue = noise3(pos * 0.2 + 5.0);
      vec3 emit = palette(hue * 1.4 + t * 0.03) * (0.25 + 0.35 * uEnergy) * (0.4 + d);
      float a = 1.0 - exp(-d * dt * 1.2);
      col += trans * emit * a * uP[3] * (1.0 + 0.4 * pulse());
      trans *= 1.0 - a;
    }
  }
  col += trans * starfield(rd);
  fragColor = vec4(col, 1.0);
}`,
};

export const cloudSea: VisualizationDef = {
  id: 'cloud-sea',
  name: 'Cloud Sea',
  description:
    'Flying low over a sea of billowing clouds at sunset. The sun swells with the bass; the clouds heave with the song’s energy.',
  tags: ['energy:low', 'style:clouds'],
  role: 'background',
  params: [
    { id: 'speed', label: 'Speed', type: 'float', min: 0, max: 3, default: 1 },
    { id: 'billow', label: 'Billow', type: 'float', min: 0.2, max: 2, default: 1 },
    {
      id: 'detail',
      label: 'Detail',
      type: 'float',
      min: 24,
      max: 96,
      default: 64,
      step: 1,
      quality: true,
    },
    { id: 'sun', label: 'Sun height', type: 'float', min: -0.05, max: 0.4, default: 0.08 },
  ],
  fragment: /* glsl */ `
${SPACE}
// Height of the cloud tops: soft rolling billows (a few octaves, drifting slowly).
float tops(vec2 xz) {
  vec3 q = vec3(xz * 0.22, uTime * 0.015);
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * noise3(q); q = q * 2.05 + vec3(3.1, 7.7, 1.3); a *= 0.5; }
  return (v - 0.45) * 1.6 * uP[1] * (0.85 + 0.3 * uEnergy);
}
void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  vec3 ro = vec3(0.0, 1.1, uTime * 0.8 * uP[0]);
  vec3 rd = normalize(vec3(uv.x, uv.y - 0.1, 1.3));
  vec3 sunDir = normalize(vec3(0.0, uP[3], 1.0));
  float sd = max(dot(rd, sunDir), 0.0);
  vec3 sky = mix(vec3(1.0, 0.62, 0.38) * 0.9, palette(0.4) * 0.35 + vec3(0.08, 0.06, 0.15), smoothstep(-0.02, 0.45, rd.y));
  vec3 sunCol = vec3(1.0, 0.8, 0.55);
  sky += sunCol * (pow(sd, 600.0) * 4.0 + pow(sd, 20.0) * 0.35) * (0.8 + 0.5 * uBass);
  vec3 col = sky;
  if (rd.y < 0.0) {
    // March the height field of cloud tops.
    float t = 0.1;
    bool hit = false;
    vec3 pos = ro;
    float steps = uP[2];
    for (int i = 0; i < 96; i++) {
      if (float(i) >= steps || t > 80.0) break;
      pos = ro + rd * t;
      float h = tops(pos.xz);
      float gap = pos.y - h;
      if (gap < 0.01) { hit = true; break; }
      t += max(0.04, gap * 0.6);
    }
    if (hit) {
      vec2 e = vec2(0.08, 0.0);
      vec3 n = normalize(vec3(tops(pos.xz - e.xy) - tops(pos.xz + e.xy), 2.0 * e.x, tops(pos.xz - e.yx) - tops(pos.xz + e.yx)));
      float lit = clamp(dot(n, normalize(sunDir + vec3(0.0, 0.6, 0.0))), 0.0, 1.0);
      float rim = pow(1.0 - max(dot(n, -rd), 0.0), 3.0) * pow(sd, 4.0);
      vec3 shade = mix(palette(0.35) * 0.25 + vec3(0.18, 0.16, 0.24), vec3(1.0, 0.9, 0.8), lit);
      vec3 cloud = shade * (0.85 + 0.25 * uLevel) + sunCol * rim * 0.8;
      col = mix(cloud, sky, 1.0 - exp(-t * 0.035));
    }
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const thunderhead: VisualizationDef = {
  id: 'thunderhead',
  name: 'Thunderhead',
  description:
    'Dark storm clouds churning overhead. Lightning flickers inside them on the hits, now and then with a bolt.',
  tags: ['energy:high', 'style:clouds'],
  role: 'background',
  params: [
    { id: 'churn', label: 'Churn', type: 'float', min: 0, max: 2, default: 0.7 },
    { id: 'lightning', label: 'Lightning', type: 'float', min: 0, max: 2, default: 1 },
    { id: 'bolts', label: 'Bolts', type: 'float', min: 0, max: 1, default: 0.5 },
  ],
  fragment: /* glsl */ `
${SPACE}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float t = uTime * 0.05 * uP[0];
  // Billowing layers: domain-warped noise, darker underneath.
  vec3 q = vec3(p * 2.2, t);
  float w = fbm3(q + fbm3(q * 1.3 + 4.0) * 1.2);
  float base = smoothstep(0.2, 0.8, w + p.y * 0.25);
  // Shade the billows: brighter where the surface faces up.
  float wUp = fbm3(q + vec3(0.0, 0.03, 0.0) + fbm3(q * 1.3 + 4.0) * 1.2);
  float relief = clamp((wUp - w) * 18.0, -1.0, 1.0);
  vec3 col = mix(vec3(0.03, 0.03, 0.05), palette(0.35) * 0.22 + vec3(0.14, 0.14, 0.17), base);
  col *= 0.85 + 0.45 * relief;
  col += vec3(0.05, 0.05, 0.07) * (0.3 + uLevel);
  // A flash somewhere in the cloud on each hit: where it lights, the cloud glows from inside.
  float beat = floor(uBeats);
  vec2 fpos = vec2(hash21(vec2(beat, 1.0)) - 0.5, hash21(vec2(beat, 2.0)) * 0.6) * vec2(1.4, 1.0);
  float flash = max(uOnset, pulse() * 0.6) * uP[1];
  float glow = exp(-length(p - fpos) * 3.0) * flash * (0.4 + w);
  col += mix(vec3(0.7, 0.75, 1.0), palette(0.8), 0.3) * glow * 1.5;
  // Occasionally a bolt to the ground.
  if (hash21(vec2(beat, 5.0)) < uP[2] && fract(uBeats) < 0.25) {
    float y = fpos.y - p.y;
    if (y > 0.0) {
      float x = fpos.x + (fbm(vec2(y * 8.0, beat)) - 0.5) * 0.25 * y * 2.0 + (fbm(vec2(y * 40.0, beat)) - 0.5) * 0.03;
      float d = abs(p.x - x);
      float fade = 1.0 - fract(uBeats) * 4.0;
      col += vec3(0.85, 0.9, 1.0) * (smoothstep(0.004, 0.0, d) + 0.25 * exp(-d * 60.0)) * fade * uP[1];
    }
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const SPACE_VISUALIZATIONS: VisualizationDef[] = [
  blackHole,
  solarEclipse,
  saturn,
  planetarium,
  nebula,
  cloudSea,
  thunderhead,
];
